import { Box, Typography, Radio, RadioGroup, FormControlLabel, TextField, InputAdornment, IconButton, Tooltip } from '@mui/material';
import { useTranslate } from 'react-admin';
import AlertInfoIcon from '@/assets/icons/alert-info-icon.svg?react';
import CreateIcon from '@/assets/icons/create-icon.svg?react';
import { Palette } from '@/themes/themeOverrides/Palette';
import { useFormContext, useWatch } from 'react-hook-form';

const createDepositTier = () => ({ id: crypto.randomUUID(), limit: '', discount: '' });
const createQuantityTier = () => ({ id: crypto.randomUUID(), limit: '', value: '' });
const getDepositTierKey = tier => tier.id ?? `deposit-${tier.limit}-${tier.discount}`;
const getQuantityTierKey = tier => tier.id ?? `quantity-${tier.limit}-${tier.value}`;

const DiscountStep = () => {
    const translate = useTranslate();
    const { setValue } = useFormContext();
    const discount = useWatch({ name: 'discount' }) || {};
    const discountType = discount?.type ?? 'fixed';
    const discountValue = discount?.fixedPercent ?? '';
    const depositTiers = Array.isArray(discount?.depositTiers) ? discount.depositTiers : [createDepositTier()];
    const quantityTiers = Array.isArray(discount?.quantityTiers) ? discount.quantityTiers : [createQuantityTier()];

    const adornmentSx = {
        '& .MuiTypography-root': {
            fontSize: '16px',
            fontWeight: 400,
            color: Palette.gray.gray500,
        },
    };

    const adornmentSmallSx = {
        '& .MuiTypography-root': {
            fontSize: '14px',
            fontWeight: 400,
            color: Palette.gray.gray500,
        },
    };

    const addTier = (fieldPath, currentTiers, emptyTier) => {
        const next = [...currentTiers, emptyTier];
        setValue(`discount.${fieldPath}`, next, { shouldDirty: true });
    };

    const updateTier = (fieldPath, currentTiers, index, key, value) => {
        const next = [...currentTiers];
        next[index] = { ...next[index], [key]: value };
        setValue(`discount.${fieldPath}`, next, { shouldDirty: true });
    };
    return (
        <Box display='flex' flexDirection='column' gap='48px' maxWidth='640px'>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '20px',
                    borderRadius: '16px',
                    backgroundColor: Palette.blue.blue50,
                    '& svg': {
                        flexShrink: 0,
                    },
                }}
            >
                <AlertInfoIcon />
                <Box className='message-box'>
                    <Typography sx={{ fontSize: '14px', fontWeight: '400', lineHeight: '20px' }}>
                        {translate('resources.campaign.labels.discountInfo')}
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ width: '492px' }}>
                <RadioGroup
                    name='discount-type'
                    value={discountType}
                    onChange={event => setValue('discount.type', event.target.value, { shouldDirty: true })}
                    sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        overflow: 'hidden',
                        '& .MuiButtonBase-root.MuiRadio-root': {
                            '&:before': {
                                borderRadius: '0 !important',
                            },
                        },
                    }}
                >
                    <FormControlLabel
                        value='fixed'
                        control={<Radio />}
                        label={translate('resources.campaign.labels.discountType.fixed')}
                        sx={{
                            margin: 0,
                            width: '100%',
                            borderLeft: 'none !important',
                        }}
                    />
                    <FormControlLabel
                        value='depositBased'
                        control={<Radio />}
                        label={translate('resources.campaign.labels.discountType.depositBased')}
                        sx={{
                            margin: 0,
                            width: '100%',
                            borderLeft: 'none !important',
                        }}
                    />
                    <FormControlLabel
                        value='quantityBased'
                        control={<Radio />}
                        label={translate('resources.campaign.labels.discountType.quantityBased')}
                        sx={{
                            margin: 0,
                            width: '100%',
                            borderLeft: 'none !important',
                        }}
                    />
                </RadioGroup>
            </Box>
            {discountType === 'fixed' && (
                <Box sx={{ maxWidth: '180px' }}>
                    <TextField
                        type='number'
                        label={translate('resources.campaign.labels.discountValue')}
                        value={discountValue}
                        onChange={event => setValue('discount.fixedPercent', event.target.value, { shouldDirty: true })}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position='end' sx={adornmentSx}>
                                    %
                                </InputAdornment>
                            ),
                            inputProps: { min: 0 },
                        }}
                        sx={{ margin: '0' }}
                    />
                </Box>
            )}

            {discountType === 'depositBased' && (
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {depositTiers.map((tier, index) => (
                        <Box key={getDepositTierKey(tier)} sx={{ display: 'flex', gap: '16px' }}>
                            <TextField
                                type='number'
                                label={translate('resources.campaign.labels.depositTierLimit')}
                                value={tier.limit}
                                onChange={event => updateTier('depositTiers', depositTiers, index, 'limit', event.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position='end' sx={adornmentSmallSx}>
                                            {translate('resources.campaign.labels.pln')}
                                        </InputAdornment>
                                    ),
                                    inputProps: { min: 0 },
                                }}
                                sx={{ flex: 1, margin: '0' }}
                            />
                            <TextField
                                type='number'
                                label={translate('resources.campaign.labels.discountValue')}
                                value={tier.discount}
                                onChange={event => updateTier('depositTiers', depositTiers, index, 'discount', event.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position='end' sx={adornmentSmallSx}>
                                            %
                                        </InputAdornment>
                                    ),
                                    inputProps: { min: 0 },
                                }}
                                sx={{ flex: 1, margin: '0' }}
                            />
                        </Box>
                    ))}
                    <Box>
                        <Tooltip placement='top' title={translate('resources.campaign.labels.addTier')}>
                            <IconButton
                                color='primary'
                                onClick={() => addTier('depositTiers', depositTiers, createDepositTier())}
                                aria-label={translate('resources.campaign.labels.addTier')}
                            >
                                <CreateIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            )}

            {discountType === 'quantityBased' && (
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {quantityTiers.map((tier, index) => (
                        <Box key={getQuantityTierKey(tier)} sx={{ display: 'flex', gap: '16px' }}>
                            <TextField
                                type='number'
                                label={translate('resources.campaign.labels.quantityTierLimit')}
                                value={tier.limit}
                                onChange={event => updateTier('quantityTiers', quantityTiers, index, 'limit', event.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position='end' sx={adornmentSmallSx}>
                                            {translate('resources.campaign.labels.pieces')}
                                        </InputAdornment>
                                    ),
                                    inputProps: { min: 0 },
                                }}
                                sx={{ flex: 1, margin: '0' }}
                            />
                            <TextField
                                type='number'
                                label={translate('resources.campaign.labels.couponValue')}
                                value={tier.value}
                                onChange={event => updateTier('quantityTiers', quantityTiers, index, 'value', event.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position='end' sx={adornmentSmallSx}>
                                            {translate('resources.campaign.labels.products')}
                                        </InputAdornment>
                                    ),
                                    inputProps: { min: 0 },
                                }}
                                sx={{ flex: 1, margin: '0' }}
                            />
                        </Box>
                    ))}
                    <Box>
                        <Tooltip placement='top' title={translate('resources.campaign.labels.addTier')}>
                            <IconButton
                                color='primary'
                                onClick={() => addTier('quantityTiers', quantityTiers, createQuantityTier())}
                                aria-label={translate('resources.campaign.labels.addTier')}
                            >
                                <CreateIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default DiscountStep;
